import { ensureDefined, SMap } from '@avantia/client-and-server-utilities';
import { lodash } from '@avantia/lodash';
import {
  createControlId,
  destructureControlId,
  HpAddressLookupActionProps,
  HpAnswerMap,
  HpAnswerValueTypes
} from '@avantia/questionset-model';
import { ReactElement, ReactNode } from 'react';
import { getAnswerValue, getKeyMapping, HpGetKeyFunction, HpKeyToFindInput } from '../../../tools/utilities';
import {
  HpClientDependentAction,
  HpClientQuestionData,
  HpClientQuestionMap,
  HpClientQuestionUiMap
} from '../../../types';
import { HpSectionGroupControlProps, SectionControl } from './sectionControl';

interface Address<ItemT> {
  addressId: string;
  propertyNameNumber: ItemT;
  addressLine1: ItemT;
  addressLine2: ItemT;
  county?: ItemT;
  townCity: ItemT;
  postcode?: ItemT;
  addressLookupButton: ItemT;
  addressEnterManuallyButton: ItemT;
  addressLookupResults?: ItemT;
  propertyNameNumberSearchTerm?: ItemT;
  postcodeSearchTerm?: ItemT;
  addressLookupJson?: ItemT;
  isCountryUk?: ItemT;
  country?: ItemT;
}

interface AddressEdit<ItemT> {
  addressEdit: ItemT;
  addressCancelEdit: ItemT;
  addressDataBeforeEdit: ItemT;
  removeAddressButton: ItemT;
}

type AddressElement = Address<ReactElement | ReactNode>;
type AddressEditElement = AddressEdit<ReactElement>;

const addressKeys: HpKeyToFindInput<AddressElement>[] = [
  'addressId',
  'propertyNameNumber',
  'addressLine1',
  'addressLine2',
  'townCity',
  { key: 'county', required: false },
  { key: 'isCountryUk', required: false },
  { key: 'country', required: false },
  { key: 'postcode', required: false },
  { key: 'addressLookupButton', required: false },
  { key: 'addressLookupResults', required: false },
  { key: 'addressEnterManuallyButton', required: false },
  { key: 'propertyNameNumberSearchTerm', required: false },
  { key: 'postcodeSearchTerm', required: false }
];

const addressEditKeys: HpKeyToFindInput<AddressEditElement>[] = [
  { key: 'addressEdit', required: false },
  { key: 'addressCancelEdit', required: false },
  { key: 'addressDataBeforeEdit', required: false },
  { key: 'removeAddressButton', required: false }
];

const addressQuestionNames: (keyof Address<string>)[] = addressKeys.map((k) => (typeof k === 'string' ? k : k.key));
const addressEditQuestionNames: (keyof AddressEdit<string>)[] = addressEditKeys.map((k) =>
  typeof k === 'string' ? k : k.key
);

export function AddressLookupControl({
  answers,
  controlMap,
  errors,
  id,
  questions,
  questionsUiMap,
  controls,
  section,
  helpInfoUI,
  index,
  label
}: HpSectionGroupControlProps): JSX.Element {
  const nameMap: SMap<ReactElement> = {};
  const nameIdMap: SMap<string> = {};
  const nonAddressGroupControls: ReactElement[] = [];
  const editGroupControls: ReactElement[] = [];
  const allAddressKeys: Partial<{
    [key in keyof Address<string>]: boolean;
  }> = Object.fromEntries(addressQuestionNames.map((k) => [k, true]));
  const allAddressEditKeys: Partial<{
    [key in keyof AddressEdit<string>]: boolean;
  }> = Object.fromEntries(addressEditQuestionNames.map((k) => [k, true]));
  allAddressKeys['addressLookupJson'] = true;

  lodash.keys(controlMap).forEach((id) => {
    const name = questions[id].name;
    nameMap[name] = controlMap[id];
    nameIdMap[name] = id;
    if (allAddressEditKeys[name]) {
      editGroupControls.push(controlMap[id]);
    } else if (!allAddressKeys[name]) {
      nonAddressGroupControls.push(controlMap[id]);
    }
  });

  if (index) {
    // We might not always have the save/edit functionality.
    const { addressEdit, getKey: getKeyFunc } = getKeyMapping<AddressEditElement, ReactElement>(
      addressEditKeys,
      nameMap
    );

    if (addressEdit) {
      const getKey = (key: keyof AddressEdit<any>): string => {
        return nameIdMap[getKeyFunc(key)];
      };

      const addressEditKey = getKey('addressEdit');
      const { answerValue: isEditing } = getAnswerValue(answers[addressEditKey], questions[addressEditKey], 'control');
      if (!isEditing) {
        return (
          <SectionControl
            controls={controls}
            answers={answers}
            controlMap={controlMap}
            errors={errors}
            helpInfoUI={helpInfoUI}
            id={id}
            index={index}
            key={id}
            label={label}
            questions={questions}
            questionsUiMap={questionsUiMap}
            section={section}
          />
        );
      }
    }
  }

  const {
    propertyNameNumber,
    postcode,
    addressLine1,
    addressLine2,
    townCity,
    county,
    addressLookupButton,
    addressEnterManuallyButton,
    addressLookupResults,
    propertyNameNumberSearchTerm,
    postcodeSearchTerm,
    country,
    isCountryUk,
    getKey: getKeyFunc
  } = getKeyMapping<AddressElement, ReactElement>(addressKeys, nameMap);

  const getKey = (key: keyof Address<any>): string => {
    return nameIdMap[getKeyFunc(key)];
  };

  const getAnswer: GetAnswerFunction = (key) => getAddressAnswer(key, getKey, questions, answers) as string | boolean;
  const resultsKey = getKey('addressLookupResults');
  const resultsQuestion = questions[resultsKey];
  const hasResults = lodash.keys((resultsQuestion.data as HpClientQuestionData).lookup).length > 0;
  const enterManually = addressEnterManuallyButton && getAnswer('addressEnterManuallyButton');
  const sectionId = `${id}-section`;
  const className = 'hp-address-section';

  let groupControl: JSX.Element | null = null;

  if (enterManually === true) {
    let addressEnterManuallyButtonControl: AddressElement | null =
      !!addressEnterManuallyButton && addressEnterManuallyButton.value;
    if (isCountryUk && isCountryUk.value && getAnswer('isCountryUk') === false) {
      addressEnterManuallyButtonControl = null;
    }

    groupControl = (
      <>
        {addressEnterManuallyButtonControl}
        {isCountryUk && isCountryUk.value}
        {propertyNameNumber.value}
        {addressLine1.value}
        {addressLine2.value}
        {townCity.value}
        {county && county.value}
        {country && country.value}
        {postcode && postcode.value}
      </>
    );
  } else {
    const lookupError = errors[getKey('addressLookupResults')];
    const showSummary =
      !lookupError &&
      addressLookupButton &&
      showAddressSummary(getKey('addressLookupButton'), questions, questionsUiMap);
    const summary = showSummary ? createAddressSummary(getAnswer) : null;
    const addressPostcode = postcodeSearchTerm || postcode;
    groupControl = (
      <>
        {!!addressEnterManuallyButton && addressEnterManuallyButton.value}
        {isCountryUk && isCountryUk.value}
        {(propertyNameNumberSearchTerm || propertyNameNumber).value}
        {addressPostcode && addressPostcode.value}
        {addressLookupButton && addressLookupButton.value}
        {hasResults && addressLookupResults.value}
        {summary && <div className="av-card av-card-child hp-address-view">{summary}</div>}
      </>
    );
  }

  if (index) {
    const addressEnterManuallyButtonKey = getKey('addressEnterManuallyButton');
    const { questionId: addressEnterManuallyButtonQId } = destructureControlId(addressEnterManuallyButtonKey, {
      requirePrefix: false
    });
    const { order: addressEnterManuallyButtonOrder } = questions[addressEnterManuallyButtonQId];

    let allControls: ReactElement[] = [groupControl];

    if (nonAddressGroupControls && nonAddressGroupControls.length > 0) {
      const { key: controlId } = nonAddressGroupControls[0];
      if (controlId) {
        const { questionId } = destructureControlId(controlId as string, { requirePrefix: false });
        const { order: firstNonAddressQuestionOrder } = questions[questionId];

        const shouldPlaceAddressGroupControlFirst = addressEnterManuallyButtonOrder < firstNonAddressQuestionOrder;
        if (shouldPlaceAddressGroupControlFirst) {
          allControls = [groupControl, ...nonAddressGroupControls];
        } else {
          allControls = [...nonAddressGroupControls, groupControl];
        }
      }
    }

    return (
      <SectionControl
        controls={[...allControls, ...editGroupControls]}
        answers={answers}
        controlMap={controlMap}
        errors={errors}
        helpInfoUI={helpInfoUI}
        id={id}
        index={index}
        key={id}
        label={label}
        questions={questions}
        questionsUiMap={questionsUiMap}
        section={section}
      />
    );
  }

  return (
    <div id={sectionId} className={className}>
      <>{groupControl}</>
    </div>
  );
}

function getAddressAnswer(
  key: keyof Address<ReactElement>,
  getControlKey: HpGetKeyFunction<AddressElement>,
  questions: HpClientQuestionMap,
  answers: HpAnswerMap
): HpAnswerValueTypes {
  const realKey = getControlKey(key);
  const { answerValue } = getAnswerValue(answers[realKey], questions[realKey], 'control');
  return answerValue;
}

type GetAnswerFunction = (key: keyof Address<ReactElement>) => string | boolean;

export function createAddressSummary(
  getAnswer: GetAnswerFunction,
  showSummary?: boolean | undefined
): ReactElement | null {
  let addressSummary: ReactElement | null = null;
  if (showSummary === undefined) {
    showSummary = !!getAnswer('addressId');
  }

  if (showSummary) {
    const propertyNameNumberText = getAnswer('propertyNameNumber') as string;
    const addressLine1Text = getAnswer('addressLine1') as string;
    const addressLine2Text = getAnswer('addressLine2') as string;
    const countyText = getAnswer('county') as string;
    const townCityText = getAnswer('townCity') as string;
    const postcodeText = getAnswer('postcode') as string;

    addressSummary = createAddressSummaryWidget({
      propertyNameNumber: propertyNameNumberText,
      addressLine1: addressLine1Text,
      addressLine2: addressLine2Text,
      townCity: townCityText,
      county: countyText,
      postcode: postcodeText
    });
  }

  return addressSummary;
}

export interface CreateAddressSummaryWidgetProps {
  propertyNameNumber: string;
  addressLine1: string;
  addressLine2?: string;
  country?: string;
  county?: string;
  postcode: string;
  townCity: string;
}

export function createAddressSummaryWidget({
  propertyNameNumber,
  addressLine1,
  addressLine2,
  county,
  postcode,
  townCity
}: CreateAddressSummaryWidgetProps) {
  const isPropNumeric = propertyNameNumber && /^\d+$/.test(propertyNameNumber as string);
  return (
    <div className="hp-address-summary-panel av-text-large">
      {propertyNameNumber && propertyNameNumber}
      {isPropNumeric ? ' ' : <br />}
      {addressLine1}
      <br />
      {addressLine2 && (
        <>
          {addressLine2}
          <br />
        </>
      )}
      {townCity}
      <br />
      {county && county}
      <br />
      {postcode}
    </div>
  );
}

function showAddressSummary(
  lookupButtonKey: string,
  questions: HpClientQuestionMap,
  questionsUiMap: HpClientQuestionUiMap
): boolean {
  let showSummary = true;
  const question = ensureDefined(questions[lookupButtonKey], `questions[${lookupButtonKey}]`);
  const dependentActions = question.data.dependentActions || [];
  const dependentAction: HpClientDependentAction | undefined = dependentActions.filter(
    (a) => a.action === 'address-lookup'
  )[0];

  if (dependentAction) {
    const { searchFields } = (dependentAction.props as HpAddressLookupActionProps) || {};
    const { groupNumber } = destructureControlId(lookupButtonKey, { requirePrefix: false });
    showSummary =
      searchFields
        .map((f) => {
          const questionId = createControlId({ questionId: f, groupNumber, options: { noPrefix: true } });
          return questionsUiMap[questionId];
        })
        .findIndex((f) => f.isDirty) < 0;
  }

  return showSummary;
}
